import { useMemo, useState } from 'react';
import type { messaging } from '@cwos/api-client';
import { useFocusTrap } from '@cwos/ui-admin';

/**
 * The composer's mention picker (F194 seam 3) — the CHANNEL ROSTER and nothing
 * wider. The list is the members the thread already loaded (`GET /channels/:id`
 * — the roster read that already exists); it is never an org-wide directory,
 * so you can only propose someone who is in the room, and the server still
 * re-validates membership on send.
 *
 * Keyboard accessible the same way the emoji picker is: rendered only while
 * open, focus enters the dialog (the filter field first), Tab walks the
 * member buttons, Escape closes and returns focus to the opener.
 */
export interface MentionPickerProps {
  members: messaging.ChannelMemberView[];
  /** Already-proposed user ids — shown as picked, not offered twice. */
  picked: ReadonlySet<string>;
  onPick: (member: messaging.ChannelMemberView) => void;
  onClose: () => void;
}

export function MentionPicker({ members, picked, onPick, onClose }: MentionPickerProps) {
  const ref = useFocusTrap<HTMLDivElement>(true, { onEscape: onClose });
  const [filter, setFilter] = useState('');

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const pool = members.filter((m) => !picked.has(m.userId));
    if (needle === '') return pool;
    return pool.filter((m) => m.userName.toLowerCase().includes(needle));
  }, [members, picked, filter]);

  return (
    <div ref={ref} className="msg-mention-pop" role="dialog" aria-label="Mention someone">
      <label className="msg-label" htmlFor="msg-mention-filter">
        Mention someone in this conversation
      </label>
      <input
        id="msg-mention-filter"
        className="msg-mention-filter"
        type="text"
        value={filter}
        placeholder="Filter members…"
        onChange={(e) => setFilter(e.target.value)}
        onKeyDown={(e) => {
          // Enter picks the single remaining match — the fast keyboard path.
          if (e.key === 'Enter' && visible.length === 1) {
            e.preventDefault();
            onPick(visible[0]!);
          }
        }}
      />
      {visible.length === 0 ? (
        <p className="rn-muted" style={{ margin: 0 }}>
          {members.length - picked.size === 0
            ? 'Everyone here is already mentioned.'
            : 'No member matches that.'}
        </p>
      ) : (
        <ul className="msg-mention-list" aria-label="Members">
          {visible.map((member) => (
            <li key={member.userId}>
              <button type="button" className="msg-mention-option" onClick={() => onPick(member)}>
                <span className="msg-mention-name">{member.userName}</span>
                {member.userRole ? <span className="rn-muted">{member.userRole}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
      <button type="button" className="rn-link msg-emoji-close" onClick={onClose}>
        Close
      </button>
    </div>
  );
}
